import { useInfiniteQuery } from '@tanstack/react-query';
import { api } from '@/lib/apiClient';
import { useAuth } from '@/context/AuthContext';
import type { SortOrder } from '@/hooks/useRoster';

/**
 * Member row from GET /roster/scoped — intentionally narrower than
 * RosterMember (the scoped endpoint returns no joinedAt/membership/health
 * fields).
 */
export interface ScopedRosterMember {
  id: string;
  displayName: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  phone: string | null;
  profileImageUrl: string | null;
}

export interface ScopedRosterFilters {
  search?: string;
  sort?: SortOrder;
}

interface ScopedRosterApiResponse {
  success: boolean;
  data: {
    members: ScopedRosterMember[];
    total: number;
  };
  meta: {
    scope: 'organization' | 'campus' | 'ministry';
    scopeLabel: string;
    role: string;
  };
}

const PAGE_SIZE = 50;

/**
 * Org-level roster for leadership roles (org_admin → whole org,
 * campus_pastor → campus, ministry_director → ministry). The backend
 * auto-detects the caller's scope; others get 403 INSUFFICIENT_ROLE.
 *
 * Scope resolution uses the request's org context, so this is gated on the
 * global org being aligned with `orgId` — the roster layout switches the
 * org before this renders (prayers pattern).
 */
export function useScopedRoster(orgId: string | undefined, filters: ScopedRosterFilters = {}) {
  const { currentOrganization, isSwitchingOrg } = useAuth();

  return useInfiniteQuery({
    queryKey: ['scoped-roster', orgId, filters],
    queryFn: async ({ pageParam }) => {
      const params = new URLSearchParams();
      params.append('limit', String(PAGE_SIZE));
      params.append('offset', String(pageParam));
      if (filters.search) params.append('search', filters.search);
      if (filters.sort) params.append('sort', filters.sort);

      const response = await api.get<ScopedRosterApiResponse>(
        `/roster/scoped?${params.toString()}`
      );
      return {
        members: response.data?.members ?? [],
        total: response.data?.total ?? 0,
        scope: response.meta?.scope ?? 'organization',
        scopeLabel: response.meta?.scopeLabel ?? '',
        role: response.meta?.role ?? '',
        offset: pageParam,
      };
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      const next = lastPage.offset + PAGE_SIZE;
      return next < lastPage.total ? next : undefined;
    },
    // Gate on !isSwitchingOrg so refetches during the claims-verification
    // window don't 403 against the new org's RLS context (useRoster pattern).
    enabled: !!orgId && !isSwitchingOrg && currentOrganization?.id === orgId,
    staleTime: 2 * 60 * 1000,
  });
}
